'use client';

import { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import BusinessFields from './BusinessFields';
import SuccessMessage from '../QuoteForm/SuccessMessage'; // Reuse SuccessMessage
import { submitQuoteAction } from '@/app/actions/quote';
import { Loader2 } from 'lucide-react';
import AltchaWidget, { AltchaErrorDetail } from '@/components/forms/AltchaWidget';
import { useSearchParams } from 'next/navigation';
import { E2E_BYPASS_KEY, E2E_BYPASS_TOKEN, isE2EBypass } from '@/lib/e2e-bypass';
import { trackEvent } from '@/lib/analytics';
import { clientLogger } from '@/lib/client-logger';
import { serializeError } from '@/lib/serialize-error';

import { businessQuoteSchema, BusinessQuoteFormSchema } from './schema';

const FORM_NAME = 'zakelijk_offerte';

interface BusinessQuoteFormProps {
	showLogo?: boolean;
	showToggle?: boolean;
}

function BusinessQuoteFormContent({ showLogo = true, showToggle = true }: BusinessQuoteFormProps) {
	const searchParams = useSearchParams();

	// E2E bypass: skip ALTCHA when ?_e2e=BREUER_E2E_2024
	const e2eBypass = isE2EBypass(searchParams.get(E2E_BYPASS_KEY));

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [altchaPayload, setAltchaPayload] = useState<string | null>(e2eBypass ? E2E_BYPASS_TOKEN : null);
	const formRef = useRef<HTMLDivElement>(null);

	const methods = useForm<BusinessQuoteFormSchema>({
		resolver: zodResolver(businessQuoteSchema) as any,
		defaultValues: {
			is_business: true,
			business_name: '',
			contact_phone: '',
			contact_email: '',
			service_category: undefined,
			location_street: '',
			location_house_number: '',
			location_postal_code: '',
			location_city: '',
			location_remarks: '',
			privacy_agreed: false,
		},
		mode: 'onSubmit',
	});

	const { handleSubmit } = methods;

	// Track form view in analytics
	useEffect(() => {
		trackEvent('form_step_view', { form_name: 'zakelijk_offerte', step: 1 });
	}, []);

	const handleAltchaError = useCallback((detail: AltchaErrorDetail) => {
		clientLogger.error('ALTCHA widget error', {
			stage: detail.stage,
			form: FORM_NAME,
			...serializeError(detail.error),
		});
	}, []);

	const onSubmit: SubmitHandler<BusinessQuoteFormSchema> = async (data) => {
		const requestId = crypto.randomUUID();

		if (!altchaPayload && !e2eBypass) {
			clientLogger.warn('Form submit blocked: ALTCHA not ready', { form: FORM_NAME, request_id: requestId });
			setError('Beveiliging wordt geladen, probeer het over een moment opnieuw.');

			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const formData = new FormData();
			formData.append('altcha', altchaPayload || E2E_BYPASS_TOKEN);
			formData.append('_request_id', requestId);

			// E2E bypass flag for server-side
			if (e2eBypass) {
				formData.append('_e2e_bypass', 'true');
			}

			// Add is_business flag explicitly
			formData.append('is_business', 'true');

			// Device metadata for lead enrichment (parsed server-side)
			formData.append('_device_ua', navigator.userAgent || '');
			formData.append('_device_screen', `${window.innerWidth}x${window.innerHeight}`);
			formData.append('_device_type', window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop');
			formData.append('_device_language', navigator.language || 'unknown');

			Object.entries(data).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					formData.append(key, String(value));
				}
			});

			// Submit with a safety timeout to prevent infinite hanging on mobile
			const result = await Promise.race([
				submitQuoteAction({ success: false }, formData),
				new Promise<never>((_resolve, reject) =>
					setTimeout(() => reject(new Error('SUBMIT_TIMEOUT')), 30_000)
				),
			]);

			if (result.success) {
				trackEvent('form_submit_success', {
					form_name: 'zakelijk_offerte',
				});
				setIsSuccess(true);
			} else {
				trackEvent('form_submit_error', {
					form_name: 'zakelijk_offerte',
					error_type: 'server_validation',
				});
				setError(result.message || 'Er is iets misgegaan. Probeer het opnieuw.');
				// Reset ALTCHA for retry
				setAltchaPayload(null);
			}
		} catch (err) {
			const isTimeout = err instanceof Error && err.message === 'SUBMIT_TIMEOUT';
			clientLogger.error('Business quote form submission failed', {
				error_type: isTimeout ? 'timeout' : 'exception',
				form: FORM_NAME,
				request_id: requestId,
				...serializeError(err),
			});
			trackEvent('form_submit_error', {
				form_name: 'zakelijk_offerte',
				error_type: isTimeout ? 'timeout' : 'catch_error',
			});
			setError(
				isTimeout
					? 'Het verzenden duurt te lang. Controleer uw internetverbinding en probeer het opnieuw.'
					: 'Er is iets misgegaan. Probeer het later opnieuw of neem telefonisch contact op.',
			);
			// Reset ALTCHA for retry
			setAltchaPayload(null);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (isSuccess) {
		return <SuccessMessage />;
	}

	return (
		<div ref={formRef} className="w-full bg-gray-50">
			{/* Header - Full Width */}
			<div className="w-full bg-blue-dark py-4 md:py-6 lg:py-8">
				<div className="max-w-6xl mx-auto px-4 grid grid-cols-2 lg:grid-cols-[auto_1fr_auto] items-center gap-4 lg:gap-6">
					{/* Toggle Switch (Mobile: Top Right | Desktop: Right) */}
					{showToggle && (
						<div className="col-start-2 row-start-1 lg:col-start-3 lg:row-start-1 justify-self-end flex-shrink-0 flex items-center p-1 rounded-full border border-white/20">
							<a
								href="/offerte"
								className="px-3 md:px-6 py-1.5 md:py-2 rounded-full text-sm md:text-base font-medium transition-all duration-200 text-white/90 hover:text-white hover:bg-white/10"
								onClick={() => trackEvent('offerte_toggle_click', { from: 'zakelijk', to: 'particulier' })}
							>
								Particulier
							</a>
							<a
								href="/zakelijk-offerte"
								className="px-3 md:px-6 py-1.5 md:py-2 rounded-full text-sm md:text-base font-bold transition-all duration-200 bg-white text-blue-dark shadow-sm cursor-default"
							>
								Zakelijk
							</a>
						</div>
					)}

					{/* Logo (Mobile: Top Left | Desktop: Left) */}
					{showLogo && (
						<a
							href="/"
							className="col-start-1 row-start-1 justify-self-start flex-shrink-0 hover:opacity-90 transition-opacity"
						>
							<img
								src="/images/breuerklimaattechniek-logo-white.png"
								alt="Breuer Klimaattechniek"
								className="h-6 md:h-10 lg:h-12 w-auto object-contain"
							/>
						</a>
					)}

					{/* Text block (Mobile: Bottom | Desktop: Center) */}
					<div className="col-span-2 row-start-2 lg:col-span-1 lg:col-start-2 lg:row-start-1 text-left lg:text-center mt-1 lg:mt-0">
						<h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-white">Zakelijk contact</h2>
						<p className="text-blue-100 mt-1 text-sm sm:text-base lg:text-lg font-medium">
							Laat uw gegevens achter — wij bellen u binnen enkele werkdagen terug
						</p>
					</div>
				</div>
			</div>

			{/* Form - Centered */}
			<div className="max-w-2xl mx-auto px-4 py-8 md:py-12">
				<FormProvider {...methods}>
					<form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-8">
						<BusinessFields />

						{/* ALTCHA Widget (invisible, Web Worker-based PoW) */}
						{!e2eBypass && <AltchaWidget onVerified={setAltchaPayload} onError={handleAltchaError} />}

						{error && <div className="p-4 bg-red-50 text-red-600 rounded-md text-sm font-medium">{error}</div>}

						<div className="pt-4">
							<Button
								type="submit"
								className="bg-orange hover:bg-orange-hover text-white w-full font-bold text-lg py-6"
								disabled={isSubmitting}
							>
								{isSubmitting ? (
									<>
										<Loader2 className="mr-2 size-5 animate-spin" /> Bezig met verzenden...
									</>
								) : (
									'Verstuur aanvraag'
								)}
							</Button>
						</div>
					</form>
				</FormProvider>
			</div>
		</div>
	);
}

export default function BusinessQuoteForm(props: BusinessQuoteFormProps) {
	return (
		<Suspense
			fallback={
				<div className="min-h-[600px] flex items-center justify-center">
					<Loader2 className="size-8 animate-spin text-blue-dark" />
				</div>
			}
		>
			<BusinessQuoteFormContent {...props} />
		</Suspense>
	);
}
